import { Badge, Button, LoadingContainer, Modal } from '@/components/ui';
import {
  sysadminService,
  type AsesoriaEdicionResponse,
  type EditarAsesoriaResponse,
  type CrearIntermediaResponse,
  type AbsorberResponse,
} from '@/services/sysadmin.service';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Gestión de una asesoría (solo sysadmin): editar orden/objetivo (y el acta ya generada), separar
 * audios en una asesoría intermedia y, en modo `absorber` (entrada aparte en la página, no una pestaña),
 * absorber una asesoría en otra. Cada vista muestra una vista previa de lo que cambia antes de aplicar
 * en producción.
 */

const PREFIJO_CORRECCION = '[CORRECCIÓN]';
const NO_VIGENTE = ['RECHAZADO', 'ELIMINADO'];
const EN_REVISION = ['EN_REVISION', 'REQUIERE_CORRECCIONES'];

const nombreActividad = (orden: number) => (orden === 1 ? 'DIAGNÓSTICO' : `Asesoría ${orden - 1}`);
const dur = (seg: number) => {
  const s = Math.round(seg || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : '', r ? `${r}s` : ''].filter(Boolean).join(' ') || '0s';
};
const horasTxt = (h: number) => `${(Math.round(h * 100) / 100).toLocaleString('es-CO')} h`;
const seg2h = (seg: number) => (seg || 0) / 3600;

type Tab = 'editar' | 'separar';
type Resultado = { ok: boolean; texto: string; avisos: string[]; /** La asesoría ya no existe: no se recarga. */ terminal?: boolean };

interface Props {
  asesoriaId: number;
  /** `absorber` abre solo la vista de absorber; `gestionar` (por defecto), editar y separar. */
  modo?: 'gestionar' | 'absorber';
  nombres: Map<number, string>;
  asesores: { asesorId: number; nombre: string; activo: boolean }[];
  onClose: () => void;
  /** Se llama tras un cambio aplicado, para que la página recargue sus casos. */
  onCambio: () => void;
}

export function GestionAsesoriaModal({ asesoriaId, modo = 'gestionar', nombres, asesores, onClose, onCambio }: Props) {
  const [data, setData] = useState<AsesoriaEdicionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('editar');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [aplicando, setAplicando] = useState(false);
  const id = asesoriaId;

  const nombre = (aid: number) => nombres.get(aid) ?? `Asesor #${aid}`;

  const cargar = useCallback(async (aid: number) => {
    setError(null);
    try {
      setData(await sysadminService.obtenerAsesoriaParaEdicion(aid));
    } catch (e: any) {
      setData(null);
      setError(e.message || 'No se pudo cargar la asesoría');
    }
  }, []);

  useEffect(() => {
    cargar(id);
  }, [id, cargar]);

  const aplicar = async (fn: () => Promise<Resultado>) => {
    setAplicando(true);
    try {
      const r = await fn();
      setResultado(r);
      onCambio();
      if (r.terminal) setData(null);
      else if (r.ok) await cargar(id);
    } catch (e: any) {
      setResultado({ ok: false, texto: e.message || 'No se pudo aplicar el cambio', avisos: [] });
    } finally {
      setAplicando(false);
    }
  };

  const ajenos = useMemo(() => (data ? data.audios.filter((a) => a.subioId !== data.asesorId) : []), [data]);

  return (
    <Modal
      isOpen
      onClose={() => !aplicando && onClose()}
      size="xl"
      title={
        data ? (
          <span>
            {modo === 'absorber' ? 'Absorber asesoría' : 'Asesoría'} #{data.asesoriaId} · {data.empresa}
          </span>
        ) : modo === 'absorber' ? (
          'Absorber asesoría'
        ) : (
          'Gestionar asesoría'
        )
      }
    >
      {!data && !error && !resultado?.terminal && <LoadingContainer text="Cargando asesoría..." />}
      {!data && resultado?.terminal && (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-sm font-medium text-green-800">
            <CheckCircle2 className="w-4 h-4" /> {resultado.texto}
          </p>
          {resultado.avisos.map((a) => (
            <p key={a} className="text-xs text-amber-800">
              • {a}
            </p>
          ))}
          <div className="flex justify-end">
            <Button onClick={onClose}>Cerrar</Button>
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
      {data && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 tabular-nums">
            <Badge variant="gray">{data.estado}</Badge>
            <span>
              {data.nombreActividad ?? 'Sin orden'}
              {data.orden != null ? ` (orden ${data.orden})` : ''}
            </span>
            <span>Dueño {nombre(data.asesorId)}</span>
            <span>Horas {horasTxt(data.horas)}</span>
            <span>{data.audios.length} audio(s)</span>
            {data.esCorreccion && <Badge variant="purple">Corrección</Badge>}
            {data.actaLink && (
              <a href={data.actaLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Ver acta
              </a>
            )}
          </div>

          {resultado && (
            <div
              className={`rounded-lg border px-3 py-2 text-xs ${
                resultado.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              <p className="flex items-center gap-1.5 font-medium">
                {resultado.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {resultado.texto}
              </p>
              {resultado.avisos.map((a) => (
                <p key={a} className="mt-0.5">
                  • {a}
                </p>
              ))}
            </div>
          )}

          {modo === 'absorber' ? (
            <TabAbsorber key={`a${data.asesoriaId}`} data={data} aplicando={aplicando} aplicar={aplicar} />
          ) : (
            <>
              <div className="flex border-b border-gray-200 gap-5">
                {(
                  [
                    ['editar', 'Editar orden y objetivo'],
                    ['separar', `Separar audios${ajenos.length ? ` (${ajenos.length} ajenos)` : ''}`],
                  ] as [Tab, string][]
                ).map(([t, label]) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`pb-2.5 text-sm font-semibold border-b-2 whitespace-nowrap ${
                      tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'editar' && (
                <TabEditar key={`e${data.asesoriaId}-${data.orden}-${data.objetivo}`} data={data} aplicando={aplicando} aplicar={aplicar} />
              )}
              {tab === 'separar' && (
                <TabSeparar
                  key={`s${data.asesoriaId}`}
                  data={data}
                  nombre={nombre}
                  asesores={asesores}
                  aplicando={aplicando}
                  aplicar={aplicar}
                />
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

type Aplicar = (fn: () => Promise<Resultado>) => Promise<void>;
type TabProps = { data: AsesoriaEdicionResponse; aplicando: boolean; aplicar: Aplicar };

function Fila({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-700">{children}</div>;
}

// ── Editar ────────────────────────────────────────────────────
function TabEditar({ data, aplicando, aplicar }: TabProps) {
  const [orden, setOrden] = useState(data.orden != null ? String(data.orden) : '');
  const [objetivo, setObjetivo] = useState(data.objetivo);
  const [actualizarActa, setActualizarActa] = useState(!!data.actaLink);

  const ordenNum = orden === '' ? null : Number(orden);
  const ordenValido = ordenNum === null || (Number.isInteger(ordenNum) && ordenNum >= 1);
  const ocupada =
    ordenNum != null ? data.asesoriasEmpresa.find((a) => a.orden === ordenNum && a.asesoriaId !== data.asesoriaId) : undefined;
  const cambioOrden = ordenNum != null && ordenNum !== data.orden;
  const cambioObjetivo = objetivo.trim() !== '' && objetivo.trim() !== data.objetivo;
  const hayCambios = cambioOrden || cambioObjetivo;

  const guardar = () =>
    aplicar(async () => {
      const r: EditarAsesoriaResponse = await sysadminService.editarAsesoria(data.asesoriaId, {
        ...(cambioOrden ? { orden: ordenNum! } : {}),
        ...(cambioObjetivo ? { objetivo: objetivo.trim() } : {}),
        actualizarActa: actualizarActa && !!data.actaLink,
      });
      const avisos = r.acta.advertencias;
      const acta = r.acta.actualizada ? ' Acta actualizada' + (r.acta.renombrada ? ' y archivo renombrado.' : '.') : '';
      return { ok: true, texto: `Cambios guardados.${acta}`, avisos };
    });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
        <div className="space-y-1">
          <label htmlFor="g-orden" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Orden
          </label>
          <input
            id="g-orden"
            type="number"
            min={1}
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-[11px] text-gray-500">
            {ordenNum && ordenValido ? <>Se llamará «{nombreActividad(ordenNum)}»</> : 'Posición dentro de la empresa'}
          </p>
          {ocupada && (
            <p className="text-[11px] text-red-600">
              Lo usa la #{ocupada.asesoriaId}. Libera ese orden primero, o crea la intermedia para que corra las siguientes.
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor="g-objetivo" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Objetivo
          </label>
          <textarea
            id="g-objetivo"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            rows={4}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
          <div>
            <Button
              size="sm"
              variant="outline"
              disabled={objetivo.startsWith(PREFIJO_CORRECCION)}
              onClick={() => setObjetivo(`${PREFIJO_CORRECCION} ${objetivo}`.trim())}
            >
              Anteponer {PREFIJO_CORRECCION}
            </Button>
          </div>
        </div>
      </div>

      <label className={`flex items-start gap-2 text-xs ${data.actaLink ? 'text-gray-700' : 'text-gray-400'}`}>
        <input
          type="checkbox"
          className="mt-0.5"
          checked={actualizarActa && !!data.actaLink}
          disabled={!data.actaLink}
          onChange={(e) => setActualizarActa(e.target.checked)}
        />
        <span>
          Actualizar también el acta ya generada. Reescribe solo las celdas «NOMBRE DE LA ACTIVIDAD» y «OBJETIVO» en el documento, sin
          regenerarlo, así que se conservan las ediciones a mano.
          {!data.actaLink && ' (Esta asesoría no tiene acta.)'}
        </span>
      </label>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 space-y-1">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Cambios</p>
        {!hayCambios && <p className="text-xs text-gray-400">Nada cambia todavía.</p>}
        {cambioOrden && (
          <Fila>
            Orden <b>{data.orden ?? '—'}</b> <ArrowRight className="w-3 h-3" /> <b>{ordenNum}</b>
            <span className="text-gray-500">
              ({data.nombreActividad ?? 'sin nombre'} → {nombreActividad(ordenNum!)})
            </span>
          </Fila>
        )}
        {cambioObjetivo && (
          <Fila>
            Objetivo: <span className="text-gray-500 line-clamp-1">{data.objetivo}</span> <ArrowRight className="w-3 h-3" />
            <b className="line-clamp-1">{objetivo.trim()}</b>
          </Fila>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={guardar} isLoading={aplicando} disabled={!hayCambios || !ordenValido || !!ocupada}>
          Guardar en producción
        </Button>
      </div>
    </div>
  );
}

// ── Separar (crear intermedia) ────────────────────────────────
function TabSeparar({
  data,
  nombre,
  asesores,
  aplicando,
  aplicar,
}: TabProps & { nombre: (id: number) => string; asesores: Props['asesores'] }) {
  const ajenosIds = data.audios.filter((a) => a.subioId !== data.asesorId).map((a) => a.audioId);
  const [sel, setSel] = useState<Set<number>>(new Set(ajenosIds));
  const [asesorId, setAsesorId] = useState('');
  const [objetivo, setObjetivo] = useState('');

  const enRevision = EN_REVISION.includes(data.estado);
  const sinAjenos = ajenosIds.length === 0;
  const elegidos = data.audios.filter((a) => sel.has(a.audioId));
  const subieron = [...new Set(elegidos.map((a) => a.subioId))];
  const necesitaAsesor = subieron.length > 1;
  const asesorFinal = asesorId ? Number(asesorId) : subieron.length === 1 ? subieron[0]! : null;

  const vigentesElegidos = elegidos.filter((a) => !NO_VIGENTE.includes(a.estado));
  const horasMovidas = vigentesElegidos.reduce((s, a) => s + seg2h(a.duracionSeg), 0);
  const horasQuedan = data.estado === 'FINALIZADA' ? Math.max(0, data.horas - horasMovidas) : null;

  const ordenNuevo = (data.orden ?? 0) + 1;
  const desplazadas = data.orden == null ? [] : data.asesoriasEmpresa.filter((a) => a.orden != null && a.orden >= ordenNuevo);

  const alternar = (id: number) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const crear = () =>
    aplicar(async () => {
      const r: CrearIntermediaResponse = await sysadminService.crearAsesoriaIntermedia(data.asesoriaId, {
        audioIds: [...sel],
        ...(asesorFinal ? { asesorId: asesorFinal } : {}),
        ...(objetivo.trim() ? { objetivo: objetivo.trim() } : {}),
      });
      return {
        ok: true,
        texto: `Asesoría #${r.asesoriaId} creada (${r.nombreActividad}, orden ${r.orden}) con ${r.audiosMovidos} audio(s). Quedó en revisión.`,
        avisos: [
          ...r.advertencias,
          ...(r.desplazadas.some((d) => d.tieneActa)
            ? [
                `Las actas de ${r.desplazadas.filter((d) => d.tieneActa).length} asesoría(s) corridas conservan su número anterior: edítalas con "Actualizar acta".`,
              ]
            : []),
        ],
      };
    });

  if (enRevision)
    return (
      <p className="text-sm text-amber-700">
        Esta asesoría está en revisión del admin ({data.estado}); resuélvela antes de separar audios.
      </p>
    );
  if (data.orden == null)
    return <p className="text-sm text-amber-700">Esta asesoría no tiene orden; ejecuta el backfill de orden primero.</p>;
  if (sinAjenos)
    return <p className="text-sm text-gray-500">Esta asesoría no tiene audios de otra persona; no hace falta una intermedia.</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Crea una asesoría <b>solo de audios</b>, justo después de esta, con los audios que marques. Queda en revisión para que el admin
        apruebe o rechace cada audio. No lleva matriz ni evidencias, así que no cambia el cumplimiento de la empresa.
      </p>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 text-left">Audio</th>
              <th className="px-3 py-2 text-left">Grabación</th>
              <th className="px-3 py-2 text-left">Duración</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Subido por</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.audios.map((a) => (
              <tr key={a.audioId} className={sel.has(a.audioId) ? 'bg-amber-50/40' : ''}>
                <td className="px-3 py-1.5">
                  <input
                    type="checkbox"
                    aria-label={`Audio ${a.audioId}`}
                    checked={sel.has(a.audioId)}
                    onChange={() => alternar(a.audioId)}
                  />
                </td>
                <td className="px-3 py-1.5 font-mono font-bold">#{a.audioId}</td>
                <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{a.fechaGrabacion}</td>
                <td className="px-3 py-1.5 font-mono whitespace-nowrap">{dur(a.duracionSeg)}</td>
                <td className="px-3 py-1.5">{a.estado}</td>
                <td className="px-3 py-1.5">
                  {nombre(a.subioId)}
                  {a.subioId === data.asesorId && (
                    <Badge variant="green" className="ml-1 text-[10px]">
                      dueño
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="g-asesor" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Asesor de la intermedia
          </label>
          <select
            id="g-asesor"
            value={asesorId}
            onChange={(e) => setAsesorId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">{subieron.length === 1 ? `Quien subió los audios (${nombre(subieron[0]!)})` : 'Elegir asesor…'}</option>
            {asesores.map((a) => (
              <option key={a.asesorId} value={a.asesorId}>
                {a.nombre}
                {a.activo ? '' : ' (inactivo)'}
              </option>
            ))}
          </select>
          {necesitaAsesor && !asesorId && <p className="text-[11px] text-red-600">Los audios elegidos los subieron varias personas.</p>}
        </div>
        <div className="space-y-1">
          <label htmlFor="g-obj-int" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Objetivo
          </label>
          <input
            id="g-obj-int"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            placeholder={`${PREFIJO_CORRECCION} Audios separados de la asesoría #${data.asesoriaId}`}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={objetivo.startsWith(PREFIJO_CORRECCION)}
            onClick={() => setObjetivo(`${PREFIJO_CORRECCION} ${objetivo}`.trim())}
          >
            Anteponer {PREFIJO_CORRECCION}
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5 space-y-1.5">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Vista previa</p>
        <Fila>
          <b>Esta asesoría (#{data.asesoriaId}):</b> {data.audios.length} audio(s) <ArrowRight className="w-3 h-3" />{' '}
          {data.audios.length - elegidos.length} audio(s)
          {horasQuedan != null ? (
            <span className="text-gray-500">
              · {horasTxt(data.horas)} <ArrowRight className="inline w-3 h-3" /> {horasTxt(horasQuedan)}
            </span>
          ) : (
            <span className="text-gray-500">· sus horas se calculan al enviarla/aprobarla</span>
          )}
        </Fila>
        <Fila>
          <b>Nueva asesoría:</b> orden {ordenNuevo} («{nombreActividad(ordenNuevo)}») · en revisión · {elegidos.length} audio(s) ·{' '}
          {horasTxt(horasMovidas)} · asesor {asesorFinal ? nombre(asesorFinal) : '—'}
        </Fila>
        {desplazadas.length > 0 && (
          <div className="pt-1">
            <p className="text-xs font-medium text-gray-700">Se corren +1 en la numeración:</p>
            <ul className="mt-1 space-y-0.5">
              {desplazadas.map((d) => (
                <li key={d.asesoriaId} className="text-xs text-gray-600 tabular-nums flex flex-wrap gap-x-2">
                  <span className="font-mono">#{d.asesoriaId}</span>
                  <span>
                    {d.nombreActividad} <ArrowRight className="inline w-3 h-3" /> {nombreActividad(d.orden! + 1)}
                  </span>
                  {d.tieneActa && <span className="text-amber-700">· su acta conserva el número anterior hasta actualizarla</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={crear} isLoading={aplicando} disabled={elegidos.length === 0 || (necesitaAsesor && !asesorId) || !asesorFinal}>
          Crear asesoría intermedia en producción
        </Button>
      </div>
    </div>
  );
}

// ── Absorber ──────────────────────────────────────────────────
function TabAbsorber({ data, aplicando, aplicar }: TabProps) {
  const [destinoId, setDestinoId] = useState('');
  const [sel, setSel] = useState<Set<number>>(new Set(data.audios.map((a) => a.audioId)));

  const destinos = data.asesoriasEmpresa.filter((a) => a.asesoriaId !== data.asesoriaId);
  const destino = destinos.find((a) => a.asesoriaId === Number(destinoId));
  const destinoBloqueado = destino ? EN_REVISION.includes(destino.estado) : false;
  const elegidos = data.audios.filter((a) => sel.has(a.audioId));
  const todos = elegidos.length === data.audios.length;
  const seElimina = todos && data.soloAudios;
  const horasMovidas = elegidos.filter((a) => !NO_VIGENTE.includes(a.estado)).reduce((s, a) => s + seg2h(a.duracionSeg), 0);
  const siguientes = seElimina && data.orden != null ? data.asesoriasEmpresa.filter((a) => a.orden != null && a.orden > data.orden!) : [];
  const pendientesAprobar = destino?.estado === 'FINALIZADA' ? elegidos.filter((a) => a.estado === 'PENDIENTE').length : 0;

  const alternar = (id: number) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const absorber = () =>
    aplicar(async () => {
      const r: AbsorberResponse = await sysadminService.absorberAsesoria(data.asesoriaId, {
        destinoId: Number(destinoId),
        audioIds: todos ? undefined : [...sel],
      });
      return {
        ok: true,
        texto: `${r.audiosMovidos} audio(s) pasaron a la #${r.destinoId}.${r.origenEliminada ? ` La #${r.origenId} quedó vacía y se eliminó.` : ''}`,
        avisos: r.advertencias,
        terminal: r.origenEliminada,
      };
    });

  if (data.audios.length === 0 && !data.soloAudios)
    return <p className="text-sm text-gray-500">Esta asesoría no tiene audios para pasar.</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Pasa audios de esta asesoría a otra de la misma empresa. Si esta es una intermedia solo de audios y pasas todos, queda vacía y se
        elimina (la numeración vuelve a como estaba).
      </p>

      <div className="space-y-1">
        <label htmlFor="g-destino" className="text-xs font-bold text-gray-500 uppercase tracking-wide">
          Asesoría destino
        </label>
        <select
          id="g-destino"
          value={destinoId}
          onChange={(e) => setDestinoId(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Elegir asesoría…</option>
          {destinos.map((a) => (
            <option key={a.asesoriaId} value={a.asesoriaId}>
              #{a.asesoriaId} · {a.nombreActividad ?? 'sin orden'} · {a.estado} · {a.audios} audio(s)
            </option>
          ))}
        </select>
        {destinoBloqueado && (
          <p className="text-[11px] text-red-600">Está en revisión del admin: no se puede mover audios hacia ella hasta que se resuelva.</p>
        )}
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-xs">
          <tbody className="divide-y divide-gray-100">
            {data.audios.map((a) => (
              <tr key={a.audioId}>
                <td className="px-3 py-1.5 w-8">
                  <input
                    type="checkbox"
                    aria-label={`Audio ${a.audioId}`}
                    checked={sel.has(a.audioId)}
                    onChange={() => alternar(a.audioId)}
                  />
                </td>
                <td className="px-3 py-1.5 font-mono font-bold">#{a.audioId}</td>
                <td className="px-3 py-1.5 tabular-nums">{a.fechaGrabacion}</td>
                <td className="px-3 py-1.5 font-mono">{dur(a.duracionSeg)}</td>
                <td className="px-3 py-1.5">{a.estado}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5 space-y-1.5">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Vista previa</p>
        {!destino ? (
          <p className="text-xs text-gray-400">Elige la asesoría destino.</p>
        ) : (
          <>
            <Fila>
              <b>{elegidos.length} audio(s)</b> ({horasTxt(horasMovidas)}) <ArrowRight className="w-3 h-3" /> #{destino.asesoriaId} (
              {destino.nombreActividad ?? 'sin orden'}, {destino.audios} <ArrowRight className="inline w-3 h-3" />{' '}
              {destino.audios + elegidos.length} audio(s))
            </Fila>
            {pendientesAprobar > 0 && (
              <p className="text-xs text-gray-600">
                {pendientesAprobar} audio(s) pendientes pasan a aprobados, porque el destino está finalizado.
              </p>
            )}
            {seElimina ? (
              <p className="text-xs text-amber-700">
                La #{data.asesoriaId} quedará vacía y se eliminará
                {siguientes.length > 0 ? `; ${siguientes.length} asesoría(s) siguientes bajan 1 en la numeración.` : '.'}
              </p>
            ) : (
              <p className="text-xs text-gray-600">
                La #{data.asesoriaId} se conserva
                {!data.soloAudios ? ' (tiene matriz, evidencias, reuniones o acta)' : ' con los audios que no marcaste'}.
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={absorber} isLoading={aplicando} disabled={!destino || destinoBloqueado || elegidos.length === 0}>
          Pasar audios en producción
        </Button>
      </div>
    </div>
  );
}
